/**
 * Inventario.
 *
 * QUE PREGUNTAS CONTESTA ESTA PANTALLA
 * ------------------------------------
 *   1. "¿Cuánta plata tengo en mercadería?" — el stock valorizado a costo de
 *      reposición, en pesos Y en dólares. Son el MISMO estante leído con dos
 *      varas, no dos plata distintas: nunca se suman.
 *   2. "¿Qué me falta?" — las alertas, de lo más grave a lo menos, con el
 *      negativo primero.
 *   3. "¿Qué le compro a cada uno?" — la reposición agrupada por proveedor, con
 *      el costo estimado de esa compra.
 *
 * EL STOCK NEGATIVO NO ES UN ERROR
 * --------------------------------
 * Significa que se vendió antes de ingresar la compra, que es lo que pasa cuando
 * llega mercadería a las siete de la tarde y se vende a las siete y cinco. El
 * sistema no lo impide —impedirlo sería impedir la venta— pero lo muestra
 * primero de todo, porque se arregla cargando el ingreso que faltó y no
 * escribiendo el número correcto encima.
 *
 * LAS OPERACIONES QUE ESCRIBEN
 * ----------------------------
 * Ingreso y salida, las dos con paso de revisión. El ingreso avisa, con el
 * número de antes y el de después, que informar un costo cambia el precio de
 * venta del producto. La salida no ofrece el tipo VENTA: esa la genera el
 * comprobante, y cargarla acá descontaría el stock dos veces.
 *
 * TRES PESTAÑAS, UNA TABLA POR PESTAÑA
 * ------------------------------------
 * Antes había cuatro secciones apiladas y tres de ellas listaban los mismos
 * productos con casi las mismas columnas: Alertas, Reposición e Inventario.
 * Un negativo aparecía tres veces en la misma pantalla y la tabla completa
 * quedaba al final del scroll. Ahora cada producto aparece una vez:
 *
 *   · «Inventario»: los totales, los contadores por estado (que filtran) y la
 *     tabla de LO QUE HAY EN EL LOCAL, con Contar e Ingresar en cada fila. Los
 *     productos en cero no entran: son lista de precios, no mercadería, y
 *     mezclados con el resto obligaban a saltear renglones para contar. Se los
 *     ve con el chip «Sin stock», y el catálogo entero está en Catálogo.
 *     Absorbe Alertas: el chip rojo dice cuántos negativos hay y los negativos
 *     van arriba de la tabla, aunque el resto se ordene por valor.
 *   · «Reposición»: qué pedirle a cada proveedor y cuánto sale. Es otra
 *     pregunta, se mira cuando se va a comprar, no cada vez que se entra.
 *   · «Movimientos»: el diario de todo lo que entró y salió, con lo que quedó
 *     después de cada uno. El mismo panel, filtrado a un producto, se abre
 *     desde la ficha en Catálogo.
 *
 * Y CONTAR, QUE NO ES UNA TERCERA OPERACIÓN. Termina en el mismo ingreso o en
 * la misma salida, con el mismo rastro; lo único que cambia es la pregunta: en
 * vez de cuántas se mueven, cuántas hay. Mientras el catálogo se ponía al día
 * fue LA operación de stock y estaba de primera en el encabezado; con el
 * relevamiento terminado, Contar es un botón más del encabezado (y está en cada
 * fila de la tabla, que es donde se cuenta un producto puntual). El remito que
 * llega se carga desde Compras.
 */
import { useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { ArrowDownToLine, ArrowUpFromLine, Boxes, ClipboardCheck } from "lucide-react";

import { PageHeader } from "@/components/layout/PageHeader";
import { Pagina } from "@/components/layout/Pagina";
import { Button } from "@/components/ui/button";
import { PieDeLista } from "@/components/ui/pie-de-lista";
import { Segmented } from "@/components/ui/segmented";
import { TituloSeccion } from "@/components/ui/titulo-seccion";
import {
  DialogConteo,
  DialogIngreso,
  DialogSalida,
  type AlcanceIngreso,
  type MovimientoDeConteo,
} from "@/components/catalogo/DialogsMovimiento";
import { mostrarCantidad } from "@/components/catalogo/Campos";
import { DialogBorrarProducto } from "@/components/catalogo/DialogBorrarProducto";
import { DialogProducto } from "@/components/catalogo/DialogProducto";
import { DialogAlta } from "@/components/alta/DialogAlta";
import { useCrearGuiado } from "@/components/alta/crear";
import { type FormAlta, type TipoAlta } from "@/components/alta/logica";
import { PorRecibir } from "@/components/encargos/PorRecibir";
import { DialogQueEntro, type QueEntro } from "@/components/catalogo/DialogQueEntro";
import { MovimientosStock } from "@/components/catalogo/MovimientosStock";
import { nombreCompleto } from "@/components/catalogo/nombre";
import { Reposicion } from "@/components/catalogo/Reposicion";
import {
  CintaValor,
  DesglosePorSeccion,
  FILTROS_STOCK_INICIALES,
  TablaStock,
  type FiltrosStockUi,
} from "@/components/catalogo/StockValorizado";
import {
  contarPorEstado,
  negativosPrimero,
  plataPorSeccion,
  totalesDeSeccion,
} from "@/components/catalogo/logica";
import { SECCIONES, filtrosDeSeccion } from "@/components/catalogo/secciones";
import type { UnidadesDeColor } from "@/components/catalogo/RepartoPorColor";
import { useIngresoRepartido } from "@/components/catalogo/repartir";
import { useCargarUsado } from "@/components/usados/cargar";
import { DialogUsado } from "@/components/usados/DialogUsado";
import {
  useActualizarProducto,
  useAlertasStock,
  useAnotarPedidoReposicion,
  useAnularPedidoReposicion,
  useConfig,
  useEliminarProducto,
  useEncargos,
  useIngresoStock,
  useProductos,
  useProveedores,
  useRecibirEncargo,
  useReposicion,
  useRubros,
  useSalidaStock,
  useSocios,
  useStock,
  useStockPorFamilia,
} from "@/lib/queries";
import { mostrarError, mostrarExito } from "@/lib/errores";
import { TOPE_FILAS, useVerMas } from "@/lib/usar-ver-mas";
import {
  aNumero,
  type CambiosProducto,
  type FiltrosStock,
  type IngresoStock,
  type Producto,
  type ResultadoMovimientoStock,
} from "@/lib/tipos";

const VISTAS = [
  { clave: "inventario", label: "Inventario" },
  { clave: "reposicion", label: "Reposición" },
  { clave: "encargos", label: "Por recibir" },
  { clave: "movimientos", label: "Movimientos" },
] as const;

type Vista = (typeof VISTAS)[number]["clave"];

/**
 * La solapa puede venir en la URL: la pastilla «para reponer» de la barra de
 * arriba abre `?vista=reposicion`, que es de donde sale su numero.
 */
export const Route = createFileRoute("/inventario")({
  validateSearch: (busqueda: Record<string, unknown>): { vista?: Vista } => {
    const vista = VISTAS.find((v) => v.clave === busqueda.vista)?.clave;
    return vista ? { vista } : {};
  },
  component: Inventario,
});

/** Cuantas filas se traen por pagina. El inventario del local entra holgado. */
const POR_PAGINA = 300;

function Inventario(): JSX.Element {
  const [filtros, setFiltros] = useState<FiltrosStockUi>(FILTROS_STOCK_INICIALES);
  // Lo que se buscó en el Ingreso antes de tocar «¿No está? Crear producto
  // nuevo»: el alta lo trae como nombre.
  const [buscadoParaAlta, setBuscadoParaAlta] = useState("");
  const [movimiento, setMovimiento] = useState<
    "cerrado" | "que-entro" | "ingreso" | "alta" | "usado" | "salida" | "conteo"
  >("cerrado");
  /**
   * El camino elegido en «¿Qué entró?». Desde la fila de un producto es "todo".
   *
   * No se llama `alcance` a secas porque esa palabra ya la usa la seccion
   * elegida en los filtros de stock (la de `CintaValor`, mas abajo): son dos
   * "alcance" distintos y compartir el nombre los pisaria entre si.
   */
  const [alcanceIngreso, setAlcanceIngreso] = useState<AlcanceIngreso>("todo");
  /** Que se mira: el inventario de hoy, o el diario de lo que entro y salio. */
  // La solapa ES la URL, sin copia en un estado: con la copia, tocar una
  // solapa no cambiaba la URL, y la pastilla «para reponer» (que lleva a
  // `?vista=reposicion`) no hacia nada si la URL ya decia eso; tampoco el
  // link «Inventario» del menu sacaba de Reposición, y al recargar se abria
  // otra solapa que la que se estaba mirando.
  const { vista = "inventario" } = Route.useSearch();
  const navegar = Route.useNavigate();
  const elegirVista = (v: Vista): void => {
    void navegar({ search: v === "inventario" ? {} : { vista: v } });
  };
  const [productoId, setProductoId] = useState<number | null>(null);
  /**
   * La ficha abierta desde una fila, y el producto que se esta por borrar.
   *
   * Inventario no edita —el stock lo mueven los movimientos— pero SI muestra cosas
   * que se editan en la ficha: el minimo y el ideal salen en cada fila y no se
   * tocan en ningun lado de esta pantalla. Abrir la ficha desde el nombre es
   * el puente; lo que se guarda sigue siendo la ficha, no la tabla.
   */
  const [enEdicion, setEnEdicion] = useState<Producto | null>(null);
  const [borrando, setBorrando] = useState<Producto | null>(null);

  // La ventana del inventario, que crece con «Ver más» y vuelve a empezar
  // cada vez que cambia un filtro.
  const { limite, verMas, reiniciar } = useVerMas(POR_PAGINA);
  useEffect(() => reiniciar(), [filtros, reiniciar]);

  // La traduccion vive en `secciones.ts`, con el resto de la regla.
  const porSeccion = useMemo(
    () => filtrosDeSeccion(filtros.seccion, filtros.rubro),
    [filtros.seccion, filtros.rubro],
  );

  /**
   * La tabla del inventario lista LO QUE HAY EN EL LOCAL, no el catalogo.
   *
   * Un producto en cero es un producto que se vende y no tiene ninguna unidad:
   * pertenece a la lista de precios, no al recuento de la mercaderia. Con los
   * ceros adentro, la tabla de Inventario era la misma lista que Catalogo con dos
   * columnas de plata al lado, y el que cuenta tenia que ir salteando renglones
   * que no le decian nada.
   *
   * Los ceros no desaparecen: el chip «Sin stock» los trae —es la pregunta
   * "¿que se me acabo?"— y el catalogo entero esta en Catalogo, que es donde
   * vive.
   */
  const filtrosServidor = useMemo<FiltrosStock>(
    () => ({
      limite,
      orden: "valor_costo_ars",
      desc: true,
      ...(filtros.texto.trim() ? { q: filtros.texto.trim() } : {}),
      ...porSeccion,
      ...(filtros.estado ? { estado: filtros.estado } : { con_stock: true }),
    }),
    [limite, filtros, porSeccion],
  );

  const { data: stock, isFetching: cargandoStock } = useStock(filtrosServidor);
  // Las alertas ya no tienen tabla propia: solo alimentan los contadores por
  // estado, que se calculan sobre todo el local y no sobre lo filtrado, para
  // que "2 negativos" siga diciendo 2 aunque se este mirando un rubro.
  const { data: alertas } = useAlertasStock({ limite: POR_PAGINA });
  // La reposicion tiene su propia ventana: se mira aparte del inventario y no
  // comparte ni filtros ni pagina con el.
  const reponer = useVerMas(POR_PAGINA);
  const { data: reposicion, isFetching: cargandoReposicion } = useReposicion({
    limite: reponer.limite,
  });
  // El desglose se pide aparte y sin filtros: es el mapa de donde esta la
  // plata, y un mapa que se recorta con el filtro puesto deja de ser un mapa.
  const { data: porFamilia } = useStockPorFamilia();
  const { data: rubros } = useRubros();
  const { data: proveedores } = useProveedores();
  const { data: config } = useConfig();
  // Los movimientos necesitan la ficha del producto (costo, markup, redondeo)
  // para poder anticipar el precio nuevo antes de guardar. Con el tope y no
  // con la pagina de la tabla: con 300 de ~330 activos, lo que viene despues
  // en el abecedario (las WALLET, algunos iPhone de color) no aparecia en el
  // buscador de Ingreso, Salida ni Contar, y contar la fila de un modelo no
  // veia las fichas de sus colores.
  const { data: productos } = useProductos({ limite: TOPE_FILAS });
  // El chequeo de IMEI repetido de «Cargar usado» necesita la lista de
  // USADOS, no la del stock: la de arriba tiene un tope, y un usado puede
  // quedar afuera cuando el catalogo lo pase. Misma consulta que Catálogo.
  const { data: usados } = useProductos({ es_usado: true, activo: true, limite: TOPE_FILAS });

  const cotizacion = aNumero(config?.cotizacion_vigente);
  const filas = useMemo(() => negativosPrimero(stock?.filas ?? []), [stock]);

  /**
   * Las familias que se ofrecen son las que tienen algun producto.
   *
   * `rubros` son las 23 del sistema, y varias no tienen ni uno: «Repuestos» y
   * «A pedido» estan vacias, y elegirlas dejaba la tabla en blanco sin explicar
   * por que. Se cuentan sobre el catalogo entero —que esta pedido sin filtros
   * para los movimientos— y no sobre las filas a la vista, porque esas ya vienen
   * filtradas por la familia elegida y la lista se derrumbaria a una sola opcion
   * apenas se elige algo.
   */
  const rubrosConProductos = useMemo(() => {
    const presentes = new Set((productos?.filas ?? []).map((p) => p.rubro_id));
    return (rubros?.filas ?? []).filter((r) => presentes.has(r.id) || r.nombre === filtros.rubro);
  }, [rubros, productos, filtros.rubro]);
  const cuenta = useMemo(() => contarPorEstado(alertas?.filas ?? []), [alertas]);

  /**
   * Los totales de arriba y el desglose son LA MISMA suma, partida distinto.
   *
   * Las dos salen de `v_stock_por_familia`, que suma sobre el inventario
   * entero. Antes el numero grande se calculaba sobre las filas traidas —300
   * de 380— y contradecia al desglose de abajo sin avisar. Elegir una seccion
   * mueve las dos cosas juntas; el buscador y el chip de estado, no: filtran la
   * tabla, que es donde se busca, y no cambian cuanta plata hay en el local.
   */
  const plata = useMemo(() => plataPorSeccion(porFamilia?.filas ?? []), [porFamilia]);
  const totales = useMemo(() => totalesDeSeccion(plata, filtros.seccion), [plata, filtros.seccion]);
  const alcance = SECCIONES.find((x) => x.clave === filtros.seccion)?.label;

  const ingresar = useIngresoStock();
  // Lo que llega puede ser lo que un cliente ya pagó: el ingreso lo avisa.
  const { data: encargosPorRecibir } = useEncargos({ estado: "POR_RECIBIR" });
  const recibirEncargo = useRecibirEncargo();
  const cargaUsado = useCargarUsado(rubros?.filas ?? []);
  const creacion = useCrearGuiado(rubros?.filas ?? [], cotizacion);

  /**
   * Lo que entró no estaba en el catálogo y se acaba de crear. Si en el alta
   * se dijeron las unidades, ya entraron ahí y no hay nada más que hacer; si
   * no, se vuelve al ingreso con el producto nuevo elegido, para la cantidad
   * y el costo.
   */
  async function crearDesdeIngreso(tipo: TipoAlta, form: FormAlta): Promise<void> {
    const nuevo = await creacion.crear(tipo, form);
    if (!nuevo) return;
    if (form.unidades && form.unidades > 0) {
      cerrar();
      return;
    }
    setProductoId(nuevo.id);
    setAlcanceIngreso("otros");
    setMovimiento("ingreso");
  }
  const actualizar = useActualizarProducto();
  // El pedido se anota con el mismo clic que abre WhatsApp (0102). Si falla,
  // el chat ya se abrio: solo se avisa, el renglon sigue sugiriendo.
  const anotarPedido = useAnotarPedidoReposicion();
  const anularPedido = useAnularPedidoReposicion();
  const eliminar = useEliminarProducto();

  /**
   * Ingresar contra las unidades de cada color, no contra el modelo.
   *
   * La logica vive en `useIngresoRepartido` porque Catalogo la necesita
   * igual: alla tambien se cuenta, y alla esta el cartel de «datos sin
   * completar» que este reparto apaga.
   */
  const repartido = useIngresoRepartido();

  async function ingresarPorColor(
    datos: IngresoStock,
    reparto: ReadonlyArray<UnidadesDeColor>,
  ): Promise<void> {
    const modelo = listaProductos.find((p) => p.id === datos.producto_id);
    if (!modelo || reparto.length === 0) return;
    try {
      const { hechos } = await repartido.ingresar(datos, reparto, listaProductos);
      cerrar();
      mostrarExito(
        `Entraron ${datos.cantidad} u. de ${nombreCompleto(modelo)}`,
        `${hechos.join(" · ")}. El modelo queda en cero, que es la lista de precios.`,
      );
    } catch (error) {
      mostrarError(error);
    }
  }

  const salir = useSalidaStock();
  const { data: socios } = useSocios();

  /**
   * Desde el encabezado, primero «¿Qué entró?». Desde la fila de un producto
   * el producto ya está dicho: se va directo al ingreso, como siempre.
   */
  function abrirIngreso(id: number | null = null): void {
    setProductoId(id);
    setAlcanceIngreso("todo");
    setMovimiento(id === null ? "que-entro" : "ingreso");
  }
  function elegirQueEntro(que: QueEntro): void {
    if (que === "usado") {
      setMovimiento("usado");
      return;
    }
    setAlcanceIngreso(que === "sellado" ? "sellados" : "otros");
    setMovimiento("ingreso");
  }
  function abrirSalida(id: number | null = null): void {
    setProductoId(id);
    setMovimiento("salida");
  }
  function abrirConteo(id: number | null = null): void {
    setProductoId(id);
    setMovimiento("conteo");
  }
  function cerrar(): void {
    setMovimiento("cerrado");
    setProductoId(null);
    setAlcanceIngreso("todo");
    cargaUsado.olvidar();
  }

  /**
   * Abrir la ficha desde una fila de la tabla.
   *
   * La fila es una `FilaStock` —la vista valorizada— y la ficha necesita el
   * `Producto` entero. Se busca en el catalogo que esta pantalla ya tiene
   * pedido para los movimientos; si no esta (la ventana del catalogo es mas
   * chica que la del stock), no se abre nada en vez de abrir una ficha vacia.
   */
  function abrirFicha(id: number): void {
    const p = listaProductos.find((x) => x.id === id);
    if (p) setEnEdicion(p);
  }

  /** Borrar se confirma con la ficha ya cerrada: un dialogo sobre otro atrapa el foco. */
  function abrirBorrado(p: Producto): void {
    setEnEdicion(null);
    setBorrando(p);
  }

  /** Re-lanza a proposito: el "tiene ventas" lo muestra el dialog, que ofrece desactivar. */
  async function borrarProducto(p: Producto): Promise<void> {
    await eliminar.mutateAsync(p.id);
    setBorrando(null);
    mostrarExito(`${p.nombre} borrado.`, "Se fue del catálogo con sus movimientos de stock.");
  }

  async function desactivarProducto(p: Producto): Promise<void> {
    try {
      await actualizar.mutateAsync({ id: p.id, cambios: { activo: false } });
      setBorrando(null);
      mostrarExito(`${p.nombre} desactivado.`, "No se vende ni aparece en las listas.");
    } catch (error) {
      mostrarError(error);
      throw error;
    }
  }

  function guardarFicha(cambios: CambiosProducto): void {
    if (!enEdicion) return;
    const p = enEdicion;
    actualizar.mutate(
      { id: p.id, cambios },
      {
        onSuccess: () => {
          setEnEdicion(null);
          mostrarExito(`${p.nombre} actualizado.`);
        },
        onError: mostrarError,
      },
    );
  }

  const listaProductos = productos?.filas ?? [];

  /** El conteo ya decidió si entra o sale; acá sólo se manda por el camino que toca. */
  function guardarConteo(m: MovimientoDeConteo): void {
    const nombre = listaProductos.find((p) => p.id === m.datos.producto_id)?.nombre;
    const listo = (r: ResultadoMovimientoStock): void => {
      cerrar();
      mostrarExito(
        `${nombre ?? "El producto"} quedó en ${mostrarCantidad(aNumero(r.stock_actual))} u.`,
        "Se registró como ajuste de inventario. El costo y el precio quedaron como estaban.",
      );
    };
    // Aparecieron equipos y se dijo de que color: cada uno va a su ficha, no a
    // la fila del modelo.
    if (m.sentido === "ingreso" && m.reparto && m.reparto.length > 0) {
      void repartirDelConteo(m.datos, m.reparto, nombre);
      return;
    }
    if (m.sentido === "ingreso")
      ingresar.mutate(m.datos, { onSuccess: listo, onError: mostrarError });
    else salir.mutate(m.datos, { onSuccess: listo, onError: mostrarError });
  }

  async function repartirDelConteo(
    datos: IngresoStock,
    reparto: ReadonlyArray<UnidadesDeColor>,
    nombre?: string,
  ): Promise<void> {
    try {
      const { hechos } = await repartido.ingresar(datos, reparto, listaProductos);
      cerrar();
      mostrarExito(
        `${nombre ?? "El modelo"} quedó repartido`,
        `${hechos.join(" · ")}. Se registró como ajuste de inventario.`,
      );
    } catch (error) {
      mostrarError(error);
    }
  }

  return (
    <Pagina>
      <PageHeader
        titulo="Inventario"
        icono={Boxes}
        acciones={
          <>
            {/* Tres botones sueltos, sin menú «Más»: sin el remito (que se
                carga desde Compras) entran los tres. */}
            <Button type="button" variant="outline" onClick={() => abrirIngreso()}>
              <ArrowDownToLine aria-hidden="true" />
              Ingreso
            </Button>
            <Button type="button" variant="outline" onClick={() => abrirSalida()}>
              <ArrowUpFromLine aria-hidden="true" />
              Salida
            </Button>
            <Button type="button" variant="outline" onClick={() => abrirConteo()}>
              <ClipboardCheck aria-hidden="true" />
              Contar
            </Button>
          </>
        }
      />

      {/* Las tres pestañas, mismo patron de solapas que /catalogo-interno. */}
      <Segmented
        apariencia="chips"
        tamano="sm"
        etiqueta="Sección de stock"
        opciones={VISTAS.map((v) => ({ valor: v.clave, etiqueta: v.label }))}
        valor={vista}
        onChange={elegirVista}
      />

      {vista === "inventario" ? (
        <>
          {/*
            DOS BLOQUES ANTES DE LA TABLA, NO SEIS.

            Eran tres tarjetas, un parrafo, un titulo con descripcion, ocho
            cajas con barras y otro titulo: media pantalla en la compu y dos
            pantallas en el celular antes del primer producto, para contestar
            dos veces la misma pregunta. Ahora la plata entra en una cinta, el
            desglose por seccion vive plegado adentro de su propio renglon, y
            la tabla arranca arriba del pliegue.
          */}
          <CintaValor
            totales={totales}
            cotizacion={cotizacion}
            alcance={alcance}
            onQuitarAlcance={() => setFiltros({ ...filtros, seccion: "", rubro: "" })}
            onVerSinStock={() => setFiltros({ ...filtros, estado: "NEGATIVO" })}
          />

          <DesglosePorSeccion
            filas={porFamilia?.filas ?? []}
            seccion={filtros.seccion}
            onSeccion={(seccion) => setFiltros({ ...filtros, seccion, rubro: "" })}
          />

          <section className="space-y-2">
            <TablaStock
              filas={filas}
              rubros={rubrosConProductos}
              filtros={filtros}
              onFiltros={setFiltros}
              cuenta={cuenta}
              cargando={cargandoStock}
              pie={
                <PieDeLista
                  visibles={filas.length}
                  total={aNumero(stock?.total)}
                  sustantivo="productos"
                  onVerMas={verMas}
                  cargando={cargandoStock}
                />
              }
              onContar={(id) => abrirConteo(id)}
              onIngresar={(id) => abrirIngreso(id)}
              onAbrirFicha={abrirFicha}
            />
          </section>
        </>
      ) : vista === "encargos" ? (
        <PorRecibir cotizacion={cotizacion} />
      ) : vista === "reposicion" ? (
        <section className="space-y-3">
          <TituloSeccion descripcion="Lo vendido que no se volvió a comprar, agrupado por proveedor, con el costo estimado.">
            Qué comprarle a cada proveedor
          </TituloSeccion>
          <Reposicion
            filas={reposicion?.filas ?? []}
            cargando={cargandoReposicion}
            onPedir={(pedido) => anotarPedido.mutate(pedido, { onError: mostrarError })}
            onAnularPedido={(id) => anularPedido.mutate(id, { onError: mostrarError })}
            onAbrirFicha={abrirFicha}
          />
          <PieDeLista
            visibles={reposicion?.filas.length ?? 0}
            total={reposicion?.total ?? 0}
            sustantivo="productos para reponer"
            onVerMas={reponer.verMas}
            cargando={cargandoReposicion}
          />
        </section>
      ) : (
        <section className="space-y-3">
          <TituloSeccion descripcion="De lo más nuevo a lo más viejo, con lo que quedó después de cada uno.">
            Todo lo que entró y salió
          </TituloSeccion>
          <MovimientosStock producto={null} />
        </section>
      )}

      <DialogConteo
        abierto={movimiento === "conteo"}
        onAbiertoChange={(a) => {
          if (!a) cerrar();
        }}
        productos={listaProductos}
        productoId={productoId}
        guardando={ingresar.isPending || salir.isPending}
        onConfirmar={guardarConteo}
      />

      {/*
          El alta guiada del Catálogo, la misma. Desde acá no se ofrece el
          iPhone (tiene su camino en «¿Qué entró?»), ni lo «a pedido» (no
          entra nada), ni el formulario completo.
      */}
      <DialogAlta
        abierto={movimiento === "alta"}
        onAbiertoChange={(a) => {
          if (!a) setMovimiento("ingreso");
        }}
        rubros={rubros?.filas ?? []}
        proveedores={proveedores?.filas ?? []}
        guardando={creacion.guardando}
        cotizacion={cotizacion}
        tipos={["accesorio", "perfume", "vaper", "celular", "equipo"]}
        conUsado={false}
        existentes={listaProductos}
        nombreInicial={buscadoParaAlta}
        onCrear={(tipo, form) => void crearDesdeIngreso(tipo, form)}
      />

      <DialogQueEntro
        abierto={movimiento === "que-entro"}
        onAbiertoChange={(a) => {
          if (!a) cerrar();
        }}
        onElegir={elegirQueEntro}
      />

      <DialogUsado
        abierto={movimiento === "usado"}
        onAbiertoChange={(a) => {
          if (!a) cerrar();
        }}
        modo="alta"
        productos={usados?.filas ?? []}
        proveedores={proveedores?.filas ?? []}
        cotizacion={cotizacion}
        guardando={cargaUsado.guardando}
        pendiente={cargaUsado.pendiente}
        onGuardar={(form) => {
          void cargaUsado.cargar(form).then((ok) => {
            if (ok) cerrar();
          });
        }}
        onReintentar={() => {
          void cargaUsado.reintentar().then((ok) => {
            if (ok) cerrar();
          });
        }}
      />

      <DialogIngreso
        abierto={movimiento === "ingreso"}
        onAbiertoChange={(a) => {
          if (!a) cerrar();
        }}
        productos={listaProductos}
        productoId={productoId}
        cotizacion={cotizacion}
        guardando={ingresar.isPending}
        alcance={alcanceIngreso}
        encargos={encargosPorRecibir?.filas ?? []}
        recibiendo={recibirEncargo.isPending}
        onRecibirEncargo={(encargo, costo, moneda) => {
          recibirEncargo.mutate(
            { id: encargo.id, costo, moneda },
            {
              onSuccess: () => {
                cerrar();
                mostrarExito(
                  `${encargo.producto} recibido`,
                  `Queda para entregar a ${encargo.cliente ?? "el cliente"} (${encargo.venta}). No sumó stock libre.`,
                );
              },
              onError: mostrarError,
            },
          );
        }}
        onCrearNuevo={
          alcanceIngreso === "otros"
            ? (buscado) => {
                setBuscadoParaAlta(buscado);
                setMovimiento("alta");
              }
            : undefined
        }
        onConfirmar={(datos, reparto) => {
          if (reparto && reparto.length > 0) {
            void ingresarPorColor(datos, reparto);
            return;
          }
          ingresar.mutate(datos, {
            onSuccess: (r) => {
              cerrar();
              const nombre = listaProductos.find((p) => p.id === datos.producto_id)?.nombre;
              mostrarExito(
                `Entraron ${datos.cantidad} u.${nombre ? ` de ${nombre}` : ""}`,
                `Queda en ${aNumero(r.stock_actual)} unidades${
                  datos.costo_unitario !== undefined && datos.actualiza_costo !== false
                    ? " · el costo de reposición y el precio de venta quedaron actualizados"
                    : ""
                }`,
              );
            },
            onError: mostrarError,
          });
        }}
      />

      {/*
          La MISMA ficha que usa Catalogo. No es una copia con menos campos:
          es el unico editor del catalogo, abierto desde la otra punta. Por eso
          no ofrece «Ir a stock» —ya estamos— pero si el borrado, que es una
          operacion del producto y tiene que estar donde este la ficha.
      */}
      <DialogProducto
        abierto={enEdicion !== null}
        onAbiertoChange={(a) => {
          if (!a) setEnEdicion(null);
        }}
        producto={enEdicion}
        productos={productos?.filas}
        rubros={rubros?.filas ?? []}
        proveedores={proveedores?.filas ?? []}
        cotizacion={cotizacion}
        guardando={actualizar.isPending}
        onCrear={() => undefined}
        onActualizar={guardarFicha}
        onContarStock={() => {
          if (!enEdicion) return;
          const id = enEdicion.id;
          setEnEdicion(null);
          abrirConteo(id);
        }}
        onBorrar={() => {
          if (enEdicion) abrirBorrado(enEdicion);
        }}
      />

      <DialogBorrarProducto
        producto={borrando}
        abierto={borrando !== null}
        onAbiertoChange={(a) => {
          if (!a) setBorrando(null);
        }}
        onEliminar={borrarProducto}
        onDesactivar={desactivarProducto}
      />

      <DialogSalida
        abierto={movimiento === "salida"}
        onAbiertoChange={(a) => {
          if (!a) cerrar();
        }}
        productos={listaProductos}
        productoId={productoId}
        socios={socios?.filas ?? []}
        guardando={salir.isPending}
        onConfirmar={(datos) =>
          salir.mutate(datos, {
            onSuccess: (r) => {
              cerrar();
              const nombre = listaProductos.find((p) => p.id === datos.producto_id)?.nombre;
              mostrarExito(
                `Salieron ${datos.cantidad} u.${nombre ? ` de ${nombre}` : ""}`,
                `Queda en ${aNumero(r.stock_actual)} unidades`,
              );
            },
            onError: mostrarError,
          })
        }
      />
    </Pagina>
  );
}
